import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import '../users/NewUser.css';

function NewUser() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();

  const loggedRole = userInfo?.role || '';
  const isSingleUser = userInfo?.is_single_user;
  const userLocationIds = userInfo?.user_location_ids || [];

  const [values, setValues] = useState({
    name: '',
    email: '',
    role: '',
    location_ids: []
  });

  const [locations, setLocations] = useState([]);
  const [emailExists, setEmailExists] = useState(false);
  const [loading, setLoading] = useState(false);

  axios.defaults.withCredentials = true;

  const canChooseLocations = loggedRole === 'super_admin' && !isSingleUser;

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/location/allLocations`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const locs = res.data.data || res.data || [];
      setLocations(Array.isArray(locs) ? locs : []);
    } catch (err) {
      console.error('Error fetching locations', err);
      setLocations([]);
    }
  }, [token]);

  useEffect(() => {
    if (canChooseLocations) {
      fetchLocations();
    }
  }, [canChooseLocations, fetchLocations]);

  const handleEmailBlur = async () => {
    if (!values.email?.trim()) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-existence`,
        {
          params: { email: values.email },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setEmailExists(res.data.exists);
    } catch (err) {
      console.error('Error checking email', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => {
      const updated = {
        ...prev,
        [name]: value
      };

      // Als gekozen rol locatie_admin wordt:
      // houd maximaal 1 locatie over
      if (name === 'role' && value === 'locatie_admin' && prev.location_ids.length > 1) {
        updated.location_ids = [prev.location_ids[0]];
      }

      return updated;
    });

    if (name === 'email') {
      setEmailExists(false);
    }
  };

  const handleLocationChange = (locId) => {
    setValues((prev) => {
      if (prev.role === 'locatie_admin') {
        return {
          ...prev,
          location_ids: [locId]
        };
      }

      const alreadySelected = prev.location_ids.includes(locId);

      return {
        ...prev,
        location_ids: alreadySelected
          ? prev.location_ids.filter((id) => id !== locId)
          : [...prev.location_ids, locId]
      };
    });
  };

  const validateForm = () => {
    if (!values.name.trim()) {
      alert('Naam is verplicht.');
      return false;
    }

    if (!values.email.trim()) {
      alert('Email is verplicht.');
      return false;
    }

    if (!values.role) {
      alert('Kies een rol.');
      return false;
    }

    if (emailExists) {
      alert('Het e-mailadres bestaat al.');
      return false;
    }

    if (canChooseLocations && values.role === 'locatie_admin' && values.location_ids.length !== 1) {
      alert('Een locatie admin moet precies één locatie hebben.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        name: values.name.trim(),
        email: values.email.trim(),
        role: values.role,
        location_ids: []
      };

      if (canChooseLocations) {
        payload.location_ids = values.location_ids;
      } else if (loggedRole === 'locatie_admin') {
        payload.location_ids = userLocationIds;
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/add`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      navigate('/users');
    } catch (err) {
      console.error('Fout bij toevoegen gebruiker', err);
      alert(err.response?.data?.message || 'Kon gebruiker niet aanmaken.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/users');

  return (
    <div className="bg-white p-4 new-user">
      <h2 className="text-center">Nieuwe Gebruiker</h2>
      <hr className="separator" />

      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col">
            <label>
              Naam <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="name"
              className="form-control rounded-0"
              required
              value={values.name}
              onChange={handleInputChange}
            />
          </div>

          <div className="col">
            <label>
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              name="email"
              className="form-control rounded-0"
              required
              value={values.email}
              onChange={handleInputChange}
              onBlur={handleEmailBlur}
            />
            {emailExists && (
              <div className="text-danger mt-1">Email is al in gebruik.</div>
            )}
          </div>
        </div>

        <div className="row mb-3">
          <div className="col">
            <label>
              Rol <span className="text-danger">*</span>
            </label>
            <select
              name="role"
              className="form-select rounded-0"
              value={values.role}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Kies rol --</option>

              {loggedRole === 'super_admin' && (
                <>
                  <option value="super_admin">Super Admin</option>
                  <option value="locatie_admin">Locatie Admin</option>
                </>
              )}

              {loggedRole === 'locatie_admin' && (
                <option value="locatie_admin">Locatie Admin</option>
              )}
            </select>
          </div>
        </div>

        {canChooseLocations && (
          <div className="mb-3">
            <label>Koppel Locaties</label>

            {values.role === 'locatie_admin' && (
              <div className="text-muted mb-2">
                Een locatie admin kan slechts één locatie beheren.
              </div>
            )}

            <div className="d-flex flex-wrap">
              {Array.isArray(locations) && locations.length > 0 ? (
                locations.map((loc) => (
                  <div key={loc.location_id} className="form-check me-3 mb-2">
                    <input
                      type={values.role === 'locatie_admin' ? 'radio' : 'checkbox'}
                      name="location_select"
                      className="form-check-input"
                      id={`loc-${loc.location_id}`}
                      checked={values.location_ids.includes(loc.location_id)}
                      onChange={() => handleLocationChange(loc.location_id)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`loc-${loc.location_id}`}
                    >
                      {loc.location_name}
                    </label>
                  </div>
                ))
              ) : (
                <p>Geen locaties beschikbaar</p>
              )}
            </div>
          </div>
        )}

        <hr className="separator" />

        <div className="row">
          <div className="col-md-6 mb-2">
            <button
              type="button"
              className="btn btn-secondary w-100 rounded-0"
              onClick={handleCancel}
            >
              Annuleren
            </button>
          </div>

          <div className="col-md-6">
            <button
              type="submit"
              className="btn btn-primary w-100 rounded-0"
              disabled={loading}
            >
              {loading ? 'Aanmaken...' : 'Aanmaken'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NewUser;